/**
 * Bundle Offer Block — Vanilla JS
 * --------------------------------------------------------------
 * Implemented as a Custom Element (<bundle-offer>) to match the
 * same architecture pattern used by Dawn (variant-selects,
 * cart-drawer, etc.). No jQuery, no external libraries.
 *
 * Responsibilities:
 *  - Selecting a bundle card (radio behaviour)
 *  - Resolving the correct variant for each independent item
 *    picker inside the selected bundle (mix & match variants)
 *  - Recalculating bundle total / compare total / savings live,
 *    entirely client-side, from the embedded variants JSON
 *  - Copying the discount code to the clipboard
 *  - Adding the bundle to the cart via the Ajax Cart API and
 *    giving the cart drawer / cart page a chance to refresh
 * --------------------------------------------------------------
 */
(function () {
  'use strict';

  if (customElements.get('bundle-offer')) return;

  class BundleOffer extends HTMLElement {
    constructor() {
      super();
      this.variants = [];
      this.cards = [];
      this.onChange = this.onChange.bind(this);
      this.onClick = this.onClick.bind(this);
    }

    connectedCallback() {
      this.parseVariants();
      this.cards = Array.from(this.querySelectorAll('[data-bundle-card]'));
      this.submitButton = this.querySelector('[data-bundle-add-to-cart]');
      this.statusEl = this.querySelector('[data-bundle-status]');

      this.addEventListener('change', this.onChange);
      this.addEventListener('click', this.onClick);

      // Sync every card's displayed numbers with the live variants
      // data on load (guards against any stale/default SSR values).
      this.cards.forEach((card) => this.recalcCard(card));
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('click', this.onClick);
    }

    parseVariants() {
      const script = this.querySelector('[data-bundle-offer-variants]');
      try {
        this.variants = script ? JSON.parse(script.textContent) : [];
      } catch (err) {
        console.error('Bundle Offer: could not parse product variants JSON.', err);
        this.variants = [];
      }
    }

    get currency() {
      return this.dataset.currency || 'USD';
    }

    get locale() {
      return this.dataset.locale || document.documentElement.lang || 'en';
    }

    formatMoney(cents) {
      const amount = (cents || 0) / 100;
      try {
        return new Intl.NumberFormat(this.locale, {
          style: 'currency',
          currency: this.currency,
        }).format(amount);
      } catch (err) {
        return '$' + amount.toFixed(2);
      }
    }

    /* -------------------------------------------------------
       Event delegation
       ------------------------------------------------------- */

    onChange(event) {
      if (event.target.matches('[data-bundle-radio]')) {
        this.selectCard(event.target);
      } else if (event.target.matches('[data-item-option]')) {
        this.updateItemVariant(event.target);
      }
    }

    onClick(event) {
      const copyBtn = event.target.closest('[data-copy-code]');
      if (copyBtn) {
        this.copyCode(copyBtn);
        return;
      }
      const addBtn = event.target.closest('[data-bundle-add-to-cart]');
      if (addBtn) {
        this.addToCart();
      }
    }

    /* -------------------------------------------------------
       Bundle selection
       ------------------------------------------------------- */

    selectCard(radio) {
      const targetCard = radio.closest('[data-bundle-card]');
      if (!targetCard) return;
      this.cards.forEach((card) => {
        card.classList.toggle('is-selected', card === targetCard);
      });
    }

    getSelectedCard() {
      return this.cards.find((card) => card.classList.contains('is-selected'));
    }

    /* -------------------------------------------------------
       Variant resolution
       ------------------------------------------------------- */

    findVariant(optionValues) {
      return this.variants.find((variant) =>
        optionValues.every((value, index) => {
          if (value === undefined || value === null || value === '') return true;
          const key = 'option' + (index + 1);
          return variant[key] === value;
        })
      );
    }

    updateItemVariant(select) {
      const row = select.closest('[data-bundle-item-row]');
      if (!row) return;

      const selects = Array.from(row.querySelectorAll('[data-item-option]')).sort(
        (a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition)
      );
      const optionValues = selects.map((s) => s.value);
      const variant = this.findVariant(optionValues);
      const hiddenInput = row.querySelector('[data-item-variant-id]');

      if (variant) {
        if (hiddenInput) hiddenInput.value = variant.id;

        const priceEl = row.querySelector('[data-item-price]');
        const availEl = row.querySelector('[data-item-availability]');
        const imgEl = row.querySelector('[data-item-image]');

        if (priceEl) priceEl.textContent = this.formatMoney(variant.price);
        if (availEl) availEl.textContent = variant.available ? '' : 'Sold out';
        if (imgEl && variant.featured_image && variant.featured_image.src) {
          imgEl.src = this.resizeImage(variant.featured_image.src, 120);
        }
      } else if (hiddenInput) {
        // No variant exists for this combination (e.g. out of stock combo).
        hiddenInput.value = '';
        const availEl = row.querySelector('[data-item-availability]');
        if (availEl) availEl.textContent = 'Unavailable';
      }

      const card = row.closest('[data-bundle-card]');
      if (card) this.recalcCard(card);
    }

    resizeImage(src, width) {
      try {
        const url = new URL(src, window.location.origin);
        url.searchParams.set('width', width);
        return url.toString();
      } catch (err) {
        return src;
      }
    }

    /**
     * Lenient collection used for live price/availability display:
     * silently skips any row that hasn't resolved to a real variant
     * yet, so the UI never throws while the shopper is mid-selection.
     */
    getCardVariantsLenient(card) {
      const rows = Array.from(card.querySelectorAll('[data-bundle-item-row]'));
      if (rows.length) {
        return rows
          .map((row) => {
            const input = row.querySelector('[data-item-variant-id]');
            const id = input ? Number(input.value) : NaN;
            return this.variants.find((v) => v.id === id);
          })
          .filter(Boolean);
      }
      return this.getSingleVariantRepeated(card);
    }

    /**
     * Strict collection used before adding to cart: returns null if
     * ANY slot hasn't resolved to a real, purchasable variant.
     */
    getCardVariantsStrict(card) {
      const rows = Array.from(card.querySelectorAll('[data-bundle-item-row]'));
      if (rows.length) {
        const resolved = [];
        for (const row of rows) {
          const input = row.querySelector('[data-item-variant-id]');
          const id = input ? Number(input.value) : NaN;
          const variant = this.variants.find((v) => v.id === id);
          if (!variant) return null;
          resolved.push(variant);
        }
        return resolved;
      }
      return this.getSingleVariantRepeated(card);
    }

    getSingleVariantRepeated(card) {
      const singleInput = card.querySelector('[data-single-variant-id]');
      const quantity = Number(card.dataset.quantity || 1);
      if (!singleInput) return null;
      const id = Number(singleInput.value);
      const variant = this.variants.find((v) => v.id === id);
      return variant ? Array(quantity).fill(variant) : null;
    }

    /* -------------------------------------------------------
       Live pricing
       ------------------------------------------------------- */

    recalcCard(card) {
      const variants = this.getCardVariantsLenient(card);
      if (!variants.length) return;

      const discountPct = Number(card.dataset.expectedDiscount || 0);

      let compareTotal = 0;
      let currentTotal = 0;
      variants.forEach((variant) => {
        const price = variant.price || 0;
        const compareAt =
          variant.compare_at_price && variant.compare_at_price > price ? variant.compare_at_price : price;
        compareTotal += compareAt;
        currentTotal += price;
      });

      const bundleTotal = Math.round(currentTotal * ((100 - discountPct) / 100));
      const savingsAmount = Math.max(0, compareTotal - bundleTotal);
      const savingsPercent =
        compareTotal > 0 && savingsAmount > 0 ? Math.round((savingsAmount / compareTotal) * 100) : 0;

      const priceEl = card.querySelector('[data-card-price]');
      const compareEl = card.querySelector('[data-card-compare]');
      const savePillEl = card.querySelector('[data-card-save-pill]');
      const savingsLine = card.querySelector('[data-card-savings-line]');
      const savingsAmountEl = card.querySelector('[data-card-savings-amount]');
      const savingsPercentEl = card.querySelector('[data-card-savings-percent]');
      const availabilityEl = card.querySelector('[data-card-availability]');
      const imageEl = card.querySelector('[data-card-image]');

      if (priceEl) priceEl.textContent = this.formatMoney(bundleTotal);

      if (compareEl) {
        const show = compareTotal > bundleTotal;
        compareEl.hidden = !show;
        if (show) compareEl.textContent = this.formatMoney(compareTotal);
      }

      if (savePillEl) {
        const show = savingsPercent > 0;
        savePillEl.hidden = !show;
        if (show) savePillEl.textContent = '-' + savingsPercent + '%';
      }

      if (savingsLine) savingsLine.hidden = savingsAmount <= 0;
      if (savingsAmountEl) savingsAmountEl.textContent = this.formatMoney(savingsAmount);
      if (savingsPercentEl) savingsPercentEl.textContent = String(savingsPercent);

      const allAvailable = variants.every((v) => v.available);
      card.classList.toggle('is-soldout', !allAvailable);
      if (availabilityEl) {
        availabilityEl.textContent = allAvailable ? 'In stock' : 'Sold out';
      }

      const firstImage = variants[0] && variants[0].featured_image;
      if (imageEl && firstImage && firstImage.src) {
        imageEl.src = this.resizeImage(firstImage.src, 240);
      }
    }

    /* -------------------------------------------------------
       Discount code copy
       ------------------------------------------------------- */

    copyCode(button) {
      const code = button.dataset.copyCode;
      if (!code) return;

      const done = () => {
        const original = button.textContent;
        button.classList.add('is-copied');
        button.textContent = 'Copied!';
        window.setTimeout(() => {
          button.classList.remove('is-copied');
          button.textContent = original;
        }, 1600);
      };

      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(code).then(done).catch(() => this.legacyCopy(code, done));
      } else {
        this.legacyCopy(code, done);
      }
    }

    legacyCopy(text, done) {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      try {
        document.execCommand('copy');
        done();
      } catch (err) {
        console.error('Bundle Offer: copy to clipboard failed.', err);
      }
      document.body.removeChild(textarea);
    }

    /* -------------------------------------------------------
       Add to cart
       ------------------------------------------------------- */

    buildLineItems(card) {
      const variants = this.getCardVariantsStrict(card);
      if (!variants) return null;

      const counts = new Map();
      variants.forEach((variant) => {
        counts.set(variant.id, (counts.get(variant.id) || 0) + 1);
      });
      return Array.from(counts.entries()).map(([id, quantity]) => ({ id, quantity }));
    }

    async addToCart() {
      const card = this.getSelectedCard();
      if (!card) {
        this.setStatus('Please choose a bundle first.', 'error');
        return;
      }

      const items = this.buildLineItems(card);
      if (!items || !items.length) {
        this.setStatus('Please select a variant for every item in the bundle.', 'error');
        return;
      }

      this.setLoading(true);
      this.setStatus('', null);

      try {
        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        const addUrl = root.replace(/\/?$/, '/') + 'cart/add.js';
        const sectionIds = this.detectCartSections();

        const payload = { items };
        if (sectionIds.length) payload.sections = sectionIds.join(',');

        const response = await fetch(addUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(payload),
        });

        const data = await response.json();

        if (!response.ok) {
          const message = data.description || data.message || 'Could not add the bundle to your cart.';
          this.setStatus(message, 'error');
          return;
        }

        this.setStatus('Bundle added to cart!', 'success');
        this.refreshCart(data);
      } catch (err) {
        console.error('Bundle Offer: add to cart request failed.', err);
        this.setStatus('Something went wrong. Please try again.', 'error');
      } finally {
        this.setLoading(false);
      }
    }

    /**
     * Best-effort detection of common cart-drawer / mini-cart section
     * ids so the Ajax "sections" param can be used when supported.
     * Safe to leave in place on any theme: unknown ids are ignored by
     * Shopify with no error.
     */
    detectCartSections() {
      const candidates = ['cart-drawer', 'cart-icon-bubble', 'cart-notification'];
      return candidates.filter(
        (id) => document.getElementById('shopify-section-' + id) || document.getElementById(id)
      );
    }

    refreshCart(addResponse) {
      // 1) Swap in section-rendered markup returned by /cart/add.js, if any.
      if (addResponse && addResponse.sections) {
        Object.keys(addResponse.sections).forEach((id) => {
          const target = document.getElementById('shopify-section-' + id) || document.getElementById(id);
          if (!target) return;
          const wrapper = document.createElement('div');
          wrapper.innerHTML = addResponse.sections[id];
          const replacement = wrapper.firstElementChild;
          if (replacement) target.replaceWith(replacement);
        });
      }

      // 2) Dawn's <cart-drawer> web component: open it if present.
      const drawer = document.querySelector('cart-drawer');
      if (drawer) {
        try {
          if (typeof drawer.open === 'function') {
            drawer.open();
          } else {
            drawer.removeAttribute('hidden');
            drawer.classList.add('active');
          }
        } catch (err) {
          /* Theme-specific API differs — safe to ignore. */
        }
      }

      // 3) Update simple cart-count bubbles and notify any listeners.
      fetch('/cart.js')
        .then((r) => r.json())
        .then((cart) => {
          document.querySelectorAll('[data-cart-count]').forEach((el) => {
            el.textContent = cart.item_count;
          });
          document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { cart } }));
        })
        .catch(() => {});
    }

    /* -------------------------------------------------------
       UI helpers
       ------------------------------------------------------- */

    setLoading(isLoading) {
      if (!this.submitButton) return;
      this.submitButton.disabled = isLoading;
      const spinner = this.submitButton.querySelector('[data-button-spinner]');
      const text = this.submitButton.querySelector('[data-button-text]');
      if (spinner) spinner.hidden = !isLoading;
      if (text) text.style.opacity = isLoading ? '0.5' : '1';
    }

    setStatus(message, state) {
      if (!this.statusEl) return;
      this.statusEl.textContent = message;
      if (state) this.statusEl.setAttribute('data-state', state);
      else this.statusEl.removeAttribute('data-state');
    }
  }

  customElements.define('bundle-offer', BundleOffer);
})();
